import { Promotion } from '@/@type/promotion';
import {
  createPromoFunc,
  hasOfferVoucherSettings,
  prepareDuplicatePromotion,
  updateEffectiveDateTime
} from '@/components/_dashboard/promotion/tools';
import { CommonTable } from '@/components/commonComponents/commonTable/commonTable';
import { ICommonTableHeadColumn } from '@/components/commonComponents/commonTable/commonTableHead/commonTableHead';
import { ISearchBox } from '@/components/commonComponents/commonTable/commonTableToolbar/commonTableToolbar';
import Scrollbar from '@/components/Scrollbar';
import { useSnackbarHelper } from '@/components/useSnackbarHelper';
import { fetchAllPromotionList } from '@/redux/slices/promotion';
import { dispatch } from '@/redux/store';
import { getErrorMessage } from '@/utils/stringHelper';
import { Card } from '@mui/material';
import { PromotionHistoryTableRow } from './promotionHistoryTableRow';

interface IPromotionTableHistoryProps {
    isLoading: boolean;
    promotions: Promotion[];
    tableTitle?: string;
}

const PROMOTION_HISTORY_TABLE_HEAD: ICommonTableHeadColumn<Promotion>[] = [
    { property: 'name', label: 'Name', align: 'left' },
    { property: 'description', label: 'Description', align: 'left' },
    { property: 'createdAt', label: 'Created At', align: 'left' },
    { property: null, label: 'Type', align: 'left' },
    { property: null, label: 'Action', align: 'right' }
];

const SEARCH_BOX: ISearchBox<Promotion> = {
    list: ['name'],
    placeholder: 'Search by name'
};

export const PromotionHistoryTable = (props: IPromotionTableHistoryProps): JSX.Element => {
    const { tableTitle, promotions, isLoading } = props;
    const { showSnackbar } = useSnackbarHelper();

    const duplicatePromotion = async (promotion: Promotion): Promise<void> => {
        if (!promotion) {
            showSnackbar('Promotion is null and duplicate fail', 'error');
            return;
        }
        try {
            const effectiveDateTime = updateEffectiveDateTime(promotion.effectiveDateTime);
            const newPromo = prepareDuplicatePromotion(promotion);

            newPromo.effectiveDateTime = effectiveDateTime;
            // if newPromo contains offerVoucherSettings, update effectiveDateTime
            if (hasOfferVoucherSettings(newPromo)) {
                newPromo.offerVoucherSettings.effectiveDateTime = effectiveDateTime;
            }

            const shouldPublish = false;
            const isSuccess = await createPromoFunc(newPromo, promotion.type, shouldPublish);
            if (isSuccess) {
                showSnackbar('Duplicate Success', 'success');
                // Refresh the promotion list to get the latest data
                await dispatch(fetchAllPromotionList()).unwrap();
                return;
            }
            showSnackbar('Duplicate Fail', 'error');
        } catch (error) {
            showSnackbar('Duplicate Fail', 'error');
            const errorMessage = getErrorMessage(error);
            console.error(errorMessage);
        }
    };

    return (
        <>
            <Card>
                <Scrollbar>
                    <CommonTable
                        searchBox={SEARCH_BOX}
                        columnList={PROMOTION_HISTORY_TABLE_HEAD}
                        dataList={promotions}
                        isLoading={isLoading}
                        tableRowKey={'id'}
                        RowBodyComponent={PromotionHistoryTableRow}
                        rowBodyProps={{ duplicatePromotion }}
                        tableTitle={tableTitle}
                    />
                </Scrollbar>
            </Card>
        </>
    );
};
